import {
  HeadingText,
  Row,
  Col,
  Button,
  Link,
  Icon,
  LoadingSpinner,
  CaptionText,
} from '@paypalcorp/pp-react'
import PropTypes from 'prop-types'
import React from 'react'
import './index.css'

const PageMessage = ({
  id = 'pageMessage',
  type,
  localization,
  onPrimaryAction,
  onSecondaryAction,
  children,
}) => {
  const {
    noteText1,
    noteText2,
    primaryText,
    primaryUrl,
    secondaryText,
    secondaryUrl,
    externalText,
    externalUrl,
    title,
  } = localization

  let primaryButton = null
  let secondaryButton = null
  let externalButton = null

  if (primaryText && primaryUrl) {
    primaryButton = (
      <Button
        className="margin"
        id={`${id}-primary-link`}
        data-testid={`${id}-primary-link`}
        as="a"
        role="button"
        href={primaryUrl}
      >
        {primaryText}
      </Button>
    )
  } else if (onPrimaryAction) {
    primaryButton = (
      <Button
        className="margin"
        id={`${id}-primary-link`}
        data-testid={`${id}-primary-link`}
        onClick={onPrimaryAction}
      >
        {primaryText}
      </Button>
    )
  }

  if (secondaryText && secondaryUrl) {
    secondaryButton = (
      <Link
        id={`${id}-secondary-link`}
        href={secondaryUrl}
        data-testid={`${id}-secondary-link`}
      >
        {secondaryText}
      </Link>
    )
  } else if (onSecondaryAction) {
    secondaryButton = (
      <Link
        id={`${id}-secondary-link`}
        data-testid={`${id}-secondary-link`}
        as="div"
        onClick={onSecondaryAction}
      >
        {secondaryText}
      </Link>
    )
  }

  if (externalText && externalUrl) {
    externalButton = (
      <Link
        id={`${id}-external-link`}
        data-testid={`${id}-external-link`}
        href={externalUrl}
      >
        {externalText}
      </Link>
    )
  }

  const style = {
    style: {
      width: '6rem',
      height: '6rem',
    },
  }
  const getIcon = () => {
    const iconId = 'pageMessage-icon'
    const className = `pageMessage__icon--${type}`
    switch (type) {
      case 'maintenance':
        return (
          <Icon
            id={iconId}
            className={className}
            name="settings"
            {...style}
            color="sysColorAccent1Main"
          />
        )
      case 'checkMark':
        return (
          <Icon
            id={iconId}
            className={className}
            name="checkmark-background"
            {...style}
            color="sysColorSuccessMain"
          />
        )
      case 'info':
        return (
          <Icon
            id={iconId}
            className={className}
            name="information"
            {...style}
            color="sysColorAccent1Main"
          />
        )
      case 'greyScale':
        return (
          <img
            src="https://www.paypalobjects.com/paypal-ui/pictograms/grayscale/svg/grayscale-1.svg"
            alt="greyscale"
            {...style}
          />
        )
      case 'checkList':
        return (
          <img
            src="https://www.paypalobjects.com/paypal-ui/pictograms/multicolored/svg/check-list.svg"
            alt="checkList"
            {...style}
          />
        )
      case 'openApln':
        return (
          <img
            src="https://www.paypalobjects.com/paypal-ui/pictograms/multicolored/svg/documents.svg"
            alt="openApln"
            {...style}
          />
        )
      case 'loading':
        return <LoadingSpinner id={iconId} className={className} {...style} />
      case 'warning':
        return (
          <Icon
            id={iconId}
            className={className}
            name="warning"
            color="sysColorWarningMain"
            {...style}
          />
        )
      case 'error':
        return (
          <Icon
            id={iconId}
            className={className}
            name="critical"
            color="sysColorErrorMain"
            {...style}
          />
        )
      case 'pending':
        return (
          <Icon id={iconId} className={className} name="clock" {...style} />
        )
      case 'question':
        return (
          <Icon
            id={iconId}
            className={className}
            color="sysColorWarningMain"
            name="question"
            {...style}
          />
        )
      case 'moreInfo':
        return (
          <Icon
            id={iconId}
            className={className}
            name="information"
            color="sysColorWarningMain"
            {...style}
          />
        )
      case 'contact':
        return (
          <Icon
            id={iconId}
            className={className}
            name="phone-2"
            color="sysColorLinkMain"
            {...style}
          />
        )
      default:
        return (
          <Icon
            id={iconId}
            className="pageMessage__icon--info"
            name="information"
            {...style}
            color="sysColorWarningMain"
          />
        )
    }
  }

  return (
    <Row>
      <Col
        sm={{ span: 12 }}
        md={{ span: 8, offset: 2 }}
        lg={{ span: 8, offset: 2 }}
      >
        <div id={id} className="pageMessage">
          <div className="icon__container">{getIcon()}</div>
          {title && (
            <HeadingText size="sm" id={`${id}-title`}>
              {title}
            </HeadingText>
          )}
          {children}
          <div className="buttonGroup">
            {primaryButton}
            {secondaryButton}
            {externalButton}
          </div>
          {noteText1 && (
            <CaptionText className="note_text" id={`${id}-note`}>
              {noteText1}
            </CaptionText>
          )}
          {noteText2 && (
            <CaptionText className="note_text" id={`${id}-note`}>
              {noteText2}
            </CaptionText>
          )}
        </div>
      </Col>
    </Row>
  )
}

PageMessage.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf([
    'maintenance',
    'checkMark',
    'greyScale',
    'checkList',
    'openApln',
    'loading',
    'warning',
    'error',
    'info',
    'pending',
    'question',
    'moreInfo',
    'contact',
  ]),
  localization: PropTypes.shape({
    primaryText: PropTypes.string,
    primaryUrl: PropTypes.string,
    secondaryText: PropTypes.string,
    secondaryUrl: PropTypes.string,
    externalText: PropTypes.string,
    externalUrl: PropTypes.string,
    title: PropTypes.node,
    noteText1: PropTypes.node,
    noteText2: PropTypes.node,
  }).isRequired,
  onPrimaryAction: PropTypes.func,
  onSecondaryAction: PropTypes.func,
  children: PropTypes.node,
}

export default PageMessage
